export const graduationStatusChoices = {
  CURRENT_FELLOW: "Current fellow",
  QUIT_REMOVED: "Quit/removed before graduation",
  READY_FOR_REFERRAL: "Ready for first referral",
  APPROVED_FOR_REFERRAL: "Approved for referral",
  FINISHED_ALGO_RESUME: "Finished Algorithms and Resume stages",
  GRADUATED_FULL: "Graduated the full program",
  NEVER_APPEARED: "Never appeared",
  SPECIAL_CASE: "Special case",
  GRADUATED_MINI: "Graduated mini program",
  EXPERIMENT: "Experiment",
  CURRENT_FELLOW_2: "Current fellow"
} as const;

export const relationshipChoices = {
  HAPPY_ENGAGED: "We are happy, they are very engaged",
  HAPPY_DISTANT: "We are happy, they are slightly distant",
  HAPPY_NOT: "We are happy, they are not",
  QUIETED_DOWN: "We quieted down",
  JUST_GRADUATED: "Just graduated",
  CURRENT_FELLOW: "Current fellow",
  STOPPED_MOTIVATION: "We stopped: motivation",
  STOPPED_CULTURE: "We stopped: culture match",
  STOPPED_ETHICS: "We stopped: different ethics",
  STOPPED_MISC: "We stopped: misc",
  EXPERIMENT: "2019-08 experiment",
  QUIT_PERSONAL: "They quit for personal reasons"
} as const;

export const programStatusChoices = {
  CURRENT_NOT_APPLYING: "Current fellow: not yet applying",
  CURRENT_FELLOW: "Current fellow",
  CURRENT_SPECIAL: "Current fellow: special circumstances",
  PAUSED_BEFORE_APPLYING: "Agreed to pause before applying",
  DISAPPEARED_BEFORE_APPLYING: "Disappeared before applying",
  QUIT_BEFORE_APPLYING: "Informed & quit before applying",
  EXPELLED: "Expelled",
  CURRENT_ACTIVELY_APPLYING: "Current fellow: actively applying",
  GRADUATED_PAUSED_ACTIVE: "Graduated, paused applying, contract active",
  GRADUATED_CONTRACT_EXPIRED: "Graduated, contract expired",
  GRADUATED_NON_QUALIFYING: "Graduated, signed a non-qualifying offer",
  GRADUATED_QUALIFYING: "Graduated, signed a qualifying offer",
  SPECIAL_2018: "2018 special contracts",
  SUSPENDED: "Suspended"
} as const;

export type GraduationStatus = keyof typeof graduationStatusChoices;
export type RelationshipStatus = keyof typeof relationshipChoices;
export type ProgramStatus = keyof typeof programStatusChoices;

// Interfaces for status options with styling
interface GraduationStatusOption {
  value: GraduationStatus
  label: string
  bgColor: string
  textColor: string
}

interface RelationshipStatusOption {
  value: RelationshipStatus
  label: string
  bgColor: string
  textColor: string
}

interface ProgramStatusOption {
  value: ProgramStatus
  label: string
  bgColor: string
  textColor: string
}

// Graduation Status Options with colors
export const graduationStatusOptions: GraduationStatusOption[] = [
  { value: 'CURRENT_FELLOW', label: 'Current fellow', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
  { value: 'QUIT_REMOVED', label: 'Quit/removed before graduation', bgColor: 'bg-red-100', textColor: 'text-red-700' },
  { value: 'READY_FOR_REFERRAL', label: 'Ready for first referral', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  { value: 'APPROVED_FOR_REFERRAL', label: 'Approved for referral', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  { value: 'FINISHED_ALGO_RESUME', label: 'Finished Algorithms and Resume stages', bgColor: 'bg-cyan-100', textColor: 'text-cyan-700' },
  { value: 'GRADUATED_FULL', label: 'Graduated the full program', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700' },
  { value: 'NEVER_APPEARED', label: 'Never appeared', bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
  { value: 'SPECIAL_CASE', label: 'Special case', bgColor: 'bg-purple-100', textColor: 'text-purple-700' },
  { value: 'GRADUATED_MINI', label: 'Graduated mini program', bgColor: 'bg-teal-100', textColor: 'text-teal-700' },
  { value: 'EXPERIMENT', label: 'Experiment', bgColor: 'bg-indigo-100', textColor: 'text-indigo-700' },
  { value: 'CURRENT_FELLOW_2', label: 'Current fellow', bgColor: 'bg-blue-100', textColor: 'text-blue-700' }
];

// Relationship Status Options with colors
export const relationshipStatusOptions: RelationshipStatusOption[] = [
  { value: 'HAPPY_ENGAGED', label: 'We are happy, they are very engaged', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  { value: 'HAPPY_DISTANT', label: 'We are happy, they are slightly distant', bgColor: 'bg-lime-100', textColor: 'text-lime-700' },
  { value: 'HAPPY_NOT', label: 'We are happy, they are not', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700' },
  { value: 'QUIETED_DOWN', label: 'We quieted down', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
  { value: 'JUST_GRADUATED', label: 'Just graduated', bgColor: 'bg-cyan-100', textColor: 'text-cyan-700' },
  { value: 'CURRENT_FELLOW', label: 'Current fellow', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
  { value: 'STOPPED_MOTIVATION', label: 'We stopped: motivation', bgColor: 'bg-red-100', textColor: 'text-red-700' },
  { value: 'STOPPED_CULTURE', label: 'We stopped: culture match', bgColor: 'bg-red-100', textColor: 'text-red-700' },
  { value: 'STOPPED_ETHICS', label: 'We stopped: different ethics', bgColor: 'bg-red-100', textColor: 'text-red-700' },
  { value: 'STOPPED_MISC', label: 'We stopped: misc', bgColor: 'bg-red-100', textColor: 'text-red-700' },
  { value: 'EXPERIMENT', label: '2019-08 experiment', bgColor: 'bg-indigo-100', textColor: 'text-indigo-700' },
  { value: 'QUIT_PERSONAL', label: 'They quit for personal reasons', bgColor: 'bg-amber-100', textColor: 'text-amber-700' }
];

// Program Status Options with colors
export const programStatusOptions: ProgramStatusOption[] = [
  { value: 'CURRENT_NOT_APPLYING', label: 'Current fellow: not yet applying', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
  { value: 'CURRENT_FELLOW', label: 'Current fellow', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
  { value: 'CURRENT_SPECIAL', label: 'Current fellow: special circumstances', bgColor: 'bg-purple-100', textColor: 'text-purple-700' },
  { value: 'PAUSED_BEFORE_APPLYING', label: 'Agreed to pause before applying', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700' },
  { value: 'DISAPPEARED_BEFORE_APPLYING', label: 'Disappeared before applying', bgColor: 'bg-red-100', textColor: 'text-red-700' },
  { value: 'QUIT_BEFORE_APPLYING', label: 'Informed & quit before applying', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
  { value: 'EXPELLED', label: 'Expelled', bgColor: 'bg-red-100', textColor: 'text-red-700' },
  { value: 'CURRENT_ACTIVELY_APPLYING', label: 'Current fellow: actively applying', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  { value: 'GRADUATED_PAUSED_ACTIVE', label: 'Graduated, paused applying, contract active', bgColor: 'bg-cyan-100', textColor: 'text-cyan-700' },
  { value: 'GRADUATED_CONTRACT_EXPIRED', label: 'Graduated, contract expired', bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
  { value: 'GRADUATED_NON_QUALIFYING', label: 'Graduated, signed a non-qualifying offer', bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
  { value: 'GRADUATED_QUALIFYING', label: 'Graduated, signed a qualifying offer', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700' },
  { value: 'SPECIAL_2018', label: '2018 special contracts', bgColor: 'bg-indigo-100', textColor: 'text-indigo-700' },
  { value: 'SUSPENDED', label: 'Suspended', bgColor: 'bg-red-100', textColor: 'text-red-700' }
];

export const  referralQualifyingStatusOptions: ProgramStatusOption[] = [
  { value: 'CURRENT_ACTIVELY_APPLYING', label: 'Current fellow: actively applying', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  { value: 'GRADUATED_PAUSED_ACTIVE', label: 'Graduated, paused applying, contract active', bgColor: 'bg-cyan-100', textColor: 'text-cyan-700' },
  { value: 'GRADUATED_CONTRACT_EXPIRED', label: 'Graduated, contract expired', bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
  { value: 'GRADUATED_NON_QUALIFYING', label: 'Graduated, signed a non-qualifying offer', bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
  { value: 'GRADUATED_QUALIFYING', label: 'Graduated, signed a qualifying offer', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700' },
];

// Helper functions for Graduation Status
export const getGraduationStatusOption = (status: string): GraduationStatusOption | undefined => {
  return graduationStatusOptions.find(option => option.value === status);
};

export const getGraduationStatusLabel = (status: string | null | undefined): string => {
  if (!status) return 'Not set';
  const option = getGraduationStatusOption(status);
  return option?.label || status;
};

export const getGraduationStatusStyles = (status: string): { bgColor: string; textColor: string } => {
  const option = getGraduationStatusOption(status);
  return {
    bgColor: option?.bgColor || 'bg-gray-100',
    textColor: option?.textColor || 'text-gray-700'
  };
};

// Helper functions for Relationship Status
export const getRelationshipStatusOption = (status: string): RelationshipStatusOption | undefined => {
  return relationshipStatusOptions.find(option => option.value === status);
};

export const getRelationshipLabel = (status: string | null | undefined): string => {
  if (!status) return 'Not set';
  const option = getRelationshipStatusOption(status);
  return option?.label || status;
};

export const getRelationshipStatusStyles = (status: string): { bgColor: string; textColor: string } => {
  const option = getRelationshipStatusOption(status);
  return {
    bgColor: option?.bgColor || 'bg-gray-100',
    textColor: option?.textColor || 'text-gray-700'
  };
};

// Helper functions for Program Status
export const getProgramStatusOption = (status: string): ProgramStatusOption | undefined => {
  return programStatusOptions.find(option => option.value === status);
};

export const getProgramStatusLabel = (status: string | null | undefined): string => {
  if (!status) return 'Not set';
  const option = getProgramStatusOption(status);
  return option?.label || status;
};

export const getProgramStatusStyles = (status: string): { bgColor: string; textColor: string } => {
  const option = getProgramStatusOption(status);
  return {
    bgColor: option?.bgColor || 'bg-gray-100',
    textColor: option?.textColor || 'text-gray-700'
  };
};
